import React, { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, UploadSimple, CheckCircle, Warning, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const FIELD_LABELS = {
  first_name: "First name",
  surname: "Surname",
  date_of_birth: "Date of birth",
  phone: "Phone",
  email: "Email",
  email2: "Email (alt)",
  lta_id: "LTA / BTM number",
  member: "Member",
  notes: "Notes",
};

export default function ImportPlayersModal({ clubId, clubName, open, onClose, onImported }) {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [step, setStep] = useState("pick"); // pick | map | done

  const reset = () => {
    setFile(null); setPreview(null); setMapping({}); setBusy(false);
    setErr(""); setResult(null); setStep("pick");
    if (fileInput.current) fileInput.current.value = "";
  };

  const close = () => { reset(); onClose?.(); };

  if (!open) return null;

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setErr(""); setPreview(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post(`/super/clubs/${clubId}/players/import/preview`, fd);
      setPreview(res.data);
      setMapping(res.data.suggested_mapping || {});
      setStep("map");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping_json", JSON.stringify(mapping));
      const res = await api.post(`/super/clubs/${clubId}/players/import/commit`, fd);
      setResult(res.data);
      setStep("done");
      onImported?.(res.data.summary);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const mappedCount = Object.values(mapping).filter((v) => !!v).length;
  const mappedValues = Object.values(mapping);
  const hasRequired = mappedValues.includes("first_name") && mappedValues.includes("surname");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" onClick={close} data-testid="import-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="surface relative w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#27272a]">
          <div>
            <div className="font-label">Bulk import · {clubName}</div>
            <h2 className="font-heading uppercase text-xl tracking-tight">Import players from Excel</h2>
          </div>
          <button onClick={close} className="btn-ghost"><X size={16} weight="bold" /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          {step === "pick" && (
            <div className="text-center py-6" data-testid="import-pick">
              <div className="w-14 h-14 rounded-full bg-[#dfff00]/10 flex items-center justify-center mx-auto mb-4">
                <UploadSimple size={28} weight="duotone" className="text-[#dfff00]" />
              </div>
              <h3 className="font-heading uppercase text-lg mb-1">Pick a .xlsx file</h3>
              <p className="text-sm text-zinc-400 mb-5 max-w-md mx-auto">
                We&apos;ll read your spreadsheet, auto-detect which column is which, then let you confirm before saving.
              </p>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.xls"
                onChange={onPickFile}
                disabled={busy}
                className="hidden"
                data-testid="import-file"
              />
              <button
                onClick={() => fileInput.current?.click()}
                disabled={busy}
                className="btn-primary"
                data-testid="import-pick-btn"
              >
                {busy ? "Reading…" : <>Choose file <ArrowRight size={14} weight="bold" /></>}
              </button>
              {err && <div className="mt-4 text-sm text-[#ff0026]" data-testid="import-error">{err}</div>}
            </div>
          )}

          {step === "map" && preview && (
            <div className="space-y-4" data-testid="import-map">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-label">{file?.name}</div>
                  <div className="text-sm text-zinc-300">
                    <span className="text-[#dfff00] font-bold">{preview.total_rows}</span> rows ·{" "}
                    <span className="text-[#16ec06] font-bold">{mappedCount}</span> columns mapped
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-[#27272a] overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 px-3 py-2 bg-[#0d0d0d] font-label">
                  <div>Your column</div>
                  <div></div>
                  <div>Maps to</div>
                </div>
                <div className="divide-y divide-[#27272a] max-h-[40vh] overflow-y-auto">
                  {preview.headers.filter((h) => h).map((h) => {
                    const value = mapping[h] || "";
                    const sampleVal = preview.sample[0]?.[h];
                    return (
                      <div key={h} className="grid grid-cols-[1fr_auto_1fr] gap-2 px-3 py-2 items-center text-sm">
                        <div className="min-w-0">
                          <div className="font-semibold text-zinc-200 truncate" data-testid={`map-header-${h}`}>{h}</div>
                          {sampleVal && <div className="text-[10px] text-zinc-500 truncate font-mono">e.g. {String(sampleVal).slice(0, 28)}</div>}
                        </div>
                        <ArrowRight size={12} className="text-zinc-600" />
                        <select
                          className="input"
                          style={{ height: "2rem", fontSize: "0.8rem" }}
                          value={value}
                          onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value || null }))}
                          data-testid={`map-select-${h}`}
                        >
                          <option value="">— ignore —</option>
                          {Object.entries(FIELD_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!hasRequired && (
                <div className="text-xs text-[#ff0026] flex items-center gap-1.5">
                  <Warning size={14} weight="fill" /> Map both First name and Surname columns before importing.
                </div>
              )}
              <div className="text-[11px] text-zinc-500">
                Players are matched by full name within this club. Existing players with the same name are skipped.
              </div>
              {err && <div className="text-sm text-[#ff0026]" data-testid="import-error">{err}</div>}
            </div>
          )}

          {step === "done" && result && (
            <div className="text-center py-4" data-testid="import-done">
              <CheckCircle size={44} weight="duotone" className="text-[#16ec06] mx-auto mb-3" />
              <h3 className="font-heading uppercase text-2xl">Import complete</h3>
              <div className="grid grid-cols-3 gap-3 mt-5 max-w-sm mx-auto">
                <Stat label="Created" value={result.summary.created} color="#16ec06" />
                <Stat label="Skipped" value={result.summary.skipped} color="#a1a1aa" />
                <Stat label="Errors" value={result.summary.errors} color="#ff0026" />
              </div>
              {result.skipped?.length > 0 && (
                <details className="mt-5 text-left max-h-40 overflow-y-auto">
                  <summary className="cursor-pointer font-label">View {result.skipped.length} skipped row{result.skipped.length === 1 ? "" : "s"}</summary>
                  <ul className="mt-2 text-xs text-zinc-400 space-y-0.5">
                    {result.skipped.map((s, i) => (
                      <li key={i}>row {s.row}: {s.name || "—"} · <span className="text-zinc-500">{s.reason}</span></li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {step === "map" && (
          <div className="p-4 border-t border-[#27272a] flex items-center justify-between gap-2">
            <button onClick={() => { reset(); setStep("pick"); }} className="btn-ghost" data-testid="import-back">
              <ArrowLeft size={14} weight="bold" /> Choose another file
            </button>
            <button
              onClick={commit}
              disabled={busy || !hasRequired}
              className="btn-primary"
              data-testid="import-commit"
            >
              {busy ? "Importing…" : <>Import {preview?.total_rows ?? ""} players <ArrowRight size={14} weight="bold" /></>}
            </button>
          </div>
        )}

        {step === "done" && (
          <div className="p-4 border-t border-[#27272a] flex justify-end">
            <button onClick={close} className="btn-primary" data-testid="import-close">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="surface p-3">
      <div className="font-label">{label}</div>
      <div className="font-heading text-3xl mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
